import { Theme, useTheme } from "@amx/ux-twoui-theming";
import { twouiZIndexes } from "@amx/ux-twoui-utils";
import { IStackStyles, Stack } from "@fluentui/react";
import React, { useLayoutEffect, useRef } from "react";
import { useComponentLogger } from "../../../shared/logging";
import { ComponentValidator } from "../../../shared/validation";
import { media, useViewport } from "../../../styles";
import { WithNavBarPageLayoutProps } from "../PageLayout.types";

const layoutWithNavBarregionsCount = 2;

const minHeight = "100vh";

const rootStyles: IStackStyles = {
    root: {
        width: "100%",
        minHeight: minHeight
    }
};

const containerStyles: IStackStyles = {
    root: {
        overflowX: "hidden",
        width: "100vw",
        minHeight: minHeight // IE 11 needs explicit height
    }
};

// These styles exist to create the mobile experiences for the LeftNav
// In the future we should explore adding these styles into the TwoUI component
// And coordinating with admin controls to create a smoother experience.
const leftNavMobileStyles = (theme: Theme): IStackStyles => {
    return {
        root: {
            position: "fixed",
            minHeight: "40px",
            zIndex: twouiZIndexes.twoUINavBar,
            width: "100%",
            backgroundColor: theme.semanticColors.bodyBackground,
        }
    };
};

const contentStyles: IStackStyles = {
    root: {
        flex: 2,
        boxSizing: "border-box",
        width: "100%",
        selectors: {
            "#uhf-footer > div": {
                paddingLeft: 0,
                paddingRight: 0
            },
            [media.maxS]: {
                // These padding values need design input to resolve.
                // Will follow-up in Bug 46293081.
                padding: "17px 16px 10px",
                width: "100%"
            },
            [media.minM]: {
                padding: "0 32px 10px",
                width: "100%" // IE 11 needs explicit width (here and below) for correct formatting
            },
            [media.minL]: {
                padding: "0 48px 10px",
                width: "100%"
            },
            [media.minXl]: {
                padding: "0 54px 10px",
                width: "100%"
            },
            [media.minXxl]: {
                padding: "0 0 10px",
                margin: "0 auto",
                maxWidth: "968px",
                width: "100%"
            },
            [media.minXxxl]: {
                padding: "0 0 10px",
                margin: "0 auto",
                maxWidth: "1184px",
                width: "100%"
            }
        }
    }
};

/** Validator that fails component if appropriate type is not provided */
export const buildColumnValidator = (columns: readonly React.ReactNode[]): ComponentValidator => (
    () => (columns.length !== layoutWithNavBarregionsCount ? `PageLayout with NavBar can only have exactly ${layoutWithNavBarregionsCount} regions` : undefined)
);

export const WithNavBarPageLayout: React.FC<WithNavBarPageLayoutProps> = ({
    messageBarContainer,
    leftNavContainer,
    children,
    id
}) => {
    useComponentLogger("WithNavBarPageLayout", id);
    const viewportType = useViewport();
    const theme = useTheme();

    const placeholderFooter = useRef<HTMLDivElement>(null);

    // Before first render, move the footer to the bottom of the second column to allow NavBar to grow to full height.
    useLayoutEffect(() => {
        if (placeholderFooter.current && placeholderFooter.current.parentNode) {
            const realFooter = document.getElementById("uhf-footer");
            if (realFooter) {
                const originalFooterParent = realFooter.parentElement!;
                placeholderFooter.current.parentNode.appendChild(realFooter);
                placeholderFooter.current.parentNode.removeChild(placeholderFooter.current);
                return () => {
                    originalFooterParent.appendChild(realFooter);
                };
            }
        }
        return undefined;
    }, []);

    return (
        <Stack horizontal={true} styles={rootStyles}>
            {viewportType !== "s" ? leftNavContainer : null}
            <Stack styles={containerStyles}>
                <main role="main" id={id} tabIndex={-1} style={{ outline: "none" }}>
                    {
                        viewportType === "s" ? (
                            <Stack styles={leftNavMobileStyles(theme)}>
                                {leftNavContainer}
                            </Stack>
                        ) : null
                    }
                    <Stack>
                        {messageBarContainer}
                    </Stack>
                    <Stack verticalAlign="start" styles={contentStyles}>
                        {children}
                        <div ref={placeholderFooter} />
                    </Stack>
                </main>
            </Stack>
        </Stack>
    );
};
